import { Box, Container, Typography, useTheme } from "@mui/material";
import CustomStepper from "../AthleteOnboarding/CustomStepper";
import { Trans, t } from "@lingui/macro";
import StepWrapper from "./StepWrapper";
import AnimatedStepTitle from "./AnimatedStepTitle";
import AnimatedGraphComponent from "./AnimatedGraphComponent";
import XAxis from "./XAxis";
import { motion } from "motion/react";
import {
  WfIconNavigationChevronLeftRegular,
  WfIconNavigationCloseRegular,
  WfButton,
} from "@WahooFitness/redesignr";
import WfActionGroup from "@WahooFitness/redesignr/dist/Actions/WfActionGroup/WfActionGroup.js";
import { useHeaderContext, GlobalHeader } from "@WahooFitness/wahoo-offline-mfe";
import { EducationStepContent } from "./types";

function EducationFlow({
  steps,
  activeStep,
  handleNext,
  handleBack,
  handleSkip,
  bodyRef,
  isStandAlone,
}: {
  steps: EducationStepContent[];
  activeStep: number;
  handleNext: () => void;
  handleBack: () => void;
  handleSkip?: () => void;
  bodyRef: React.RefObject<HTMLElement | null>;
  isStandAlone: boolean;
}) {
  const { palette } = useTheme();
  const { navigateBack } = useHeaderContext();

  return (
    // wf-self-scrolling-page suppresses the shell's page-end spacer: this page fills
    // the scrollport and handles its own bottom clearance via the sticky action bar,
    // so the spacer would only add empty scrollable space below it.
    <Container
      className="wf-self-scrolling-page"
      maxWidth="sm"
      disableGutters
      sx={{ minHeight: "100%", display: "flex", flexDirection: "column" }}
    >
      <Box display="flex" flexDirection="column" flexGrow={1} bgcolor="background.level1">
        <GlobalHeader
          title={steps[activeStep].headerTitle}
          primaryLeftActionItem={{
            icon: (
              <WfIconNavigationChevronLeftRegular
                sx={
                  isStandAlone && activeStep === 0
                    ? { color: palette.background.level1 }
                    : undefined
                }
              />
            ),
            action: handleBack,
            disabled: isStandAlone && activeStep === 0,
          }}
          primaryRightActionItem={
            isStandAlone
              ? {
                  icon: <WfIconNavigationCloseRegular />,
                  action: navigateBack,
                }
              : {
                  text: t`Skip`,
                  action: handleSkip,
                }
          }
        />

        <Box
          flexGrow={1}
          sx={{ boxShadow: `0 0 0 100vmax ${palette.background.level1}` }}
          bgcolor={palette.background.level1}
        >
          <>
            <Box
              display="flex"
              position="absolute"
              left={0}
              pl={2}
              py={1}
              zIndex={1}
              bgcolor={palette.background.level1}
            >
              <CustomStepper steps={steps} activeStep={activeStep} />
            </Box>

            <Box bgcolor={palette.background.level1} px={2} ref={bodyRef}>
              <StepWrapper>
                <AnimatedStepTitle title={steps[activeStep].headline} step={activeStep} />
                <AnimatedGraphComponent steps={steps} activeStep={activeStep} />
              </StepWrapper>

              <Box position="relative" width="100%">
                {steps[activeStep].ticks.length > 0 && <XAxis ticks={steps[activeStep].ticks} />}
              </Box>
              <Box width="100%">
                <Typography
                  mt={2}
                  key={`description-${steps[activeStep].stepId}`}
                  color="text.primary"
                  component={motion.span}
                  variant="prose-sm"
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ type: "spring", damping: 20, stiffness: 300 }}
                  display="block"
                >
                  {steps[activeStep].description}
                </Typography>
              </Box>
            </Box>
          </>
        </Box>

        <WfActionGroup orientation="vertical" fullWidth sticky>
          <WfButton
            hierarchy="primary"
            size="large"
            fullWidth
            onClick={handleNext}
            disabled={steps[activeStep].disabled}
          >
            <Trans>{steps[activeStep].buttonName}</Trans>
          </WfButton>
        </WfActionGroup>
      </Box>
    </Container>
  );
}

export default EducationFlow;
